// frontend/src/reducers/paymentReducers.js

import * as actionTypes from "../constants/paymentConstants";

function createAsyncState(extra = {}) {
  return {
    loading: false,
    success: false,
    error: null,
    errorCode: "",
    status: 0,
    requestId: "",
    retryAfterSeconds: 0,
    message: "",
    ...extra,
  };
}

function createCurrentPaymentState() {
  return {
    order: null,
    orderNumber: "",
    payment: null,
    clientSecret: "",
    customerCheckout: null,
    paymentWindowExpiresAt: "",
    approvalUrl: "",
    approveUrl: "",
    payerActionUrl: "",
    checkoutUrl: "",
    nextAction: null,
  };
}

function createInitialState() {
  return {
    config: createAsyncState({
      loaded: false,
      enabledProviders: [],
      stripeEnabled: false,
      stripePublicKey: "",
      stripePublishableKey: "",
      paypalEnabled: false,
      paypalClientId: "",
      sumupEnabled: false,
      providers: {},
    }),

    getOrderPayment: createAsyncState({
      ...createCurrentPaymentState(),
    }),

    orderPayment: createAsyncState({
      ...createCurrentPaymentState(),
      reused: false,
      ignored: false,
      idempotencyKey: "",
    }),

    cancelPending: createAsyncState({
      order: null,
      orderNumber: "",
      payment: null,
      customerCheckout: null,
      paymentWindowExpiresAt: "",
      paymentAction: null,
      idempotencyKey: "",
    }),

    confirmPayment: createAsyncState({
      ...createCurrentPaymentState(),
      idempotencyKey: "",
    }),

    current: createCurrentPaymentState(),
  };
}

const initialState = createInitialState();

function safeText(value = "") {
  return String(value ?? "").trim();
}

function customerSafeMessage(value = "", fallback = "") {
  const text = safeText(value);

  if (!text) return fallback;

  const lower = text.toLowerCase();

  if (
    lower.includes("reserved") ||
    lower.includes("temporarily reserved") ||
    lower.includes("allocated") ||
    lower.includes("held") ||
    lower.includes("held for")
  ) {
    return fallback;
  }

  return text;
}

function requestBranch(branch = {}) {
  return {
    ...branch,
    loading: true,
    success: false,
    error: null,
    errorCode: "",
    status: 0,
    requestId: "",
    retryAfterSeconds: 0,
    message: "",
  };
}

function successBranch(branch = {}, payload = {}, extra = {}) {
  return {
    ...branch,
    loading: false,
    success: true,
    error: null,
    errorCode: "",
    status: 0,
    requestId: "",
    retryAfterSeconds: 0,
    message: customerSafeMessage(payload?.message, ""),
    ...extra,
  };
}

function failBranch(branch = {}, payload = {}) {
  return {
    ...branch,
    loading: false,
    success: false,
    error: customerSafeMessage(payload?.message, "Request failed."),
    errorCode: safeText(payload?.code),
    status: Number(payload?.status || 0),
    requestId: safeText(payload?.requestId),
    retryAfterSeconds: Number(payload?.retryAfterSeconds || 0),
    message: "",
  };
}

function getProviderConfig(payload = {}, provider = "") {
  return payload?.providers?.[provider] || payload?.[provider] || {};
}

function getStripePublicKey(payload = {}) {
  const stripe = getProviderConfig(payload, "stripe");

  return safeText(
    payload?.stripePublicKey ||
      payload?.stripePublishableKey ||
      stripe?.publicKey ||
      stripe?.publishableKey,
  );
}

function getPayPalClientId(payload = {}) {
  const paypal = getProviderConfig(payload, "paypal");

  return safeText(payload?.paypalClientId || paypal?.clientId);
}

function getSumUpEnabled(payload = {}) {
  const sumup = getProviderConfig(payload, "sumup");

  return Boolean(payload?.sumupEnabled || sumup?.enabled);
}

function getCustomerCheckout(payload = {}) {
  return payload?.customerCheckout || payload?.order?.customerCheckout || null;
}

function getPaymentWindowExpiresAt(payload = {}) {
  return safeText(
    payload?.paymentWindowExpiresAt ||
      payload?.customerCheckout?.paymentWindowExpiresAt ||
      payload?.order?.paymentWindowExpiresAt ||
      payload?.order?.customerCheckout?.paymentWindowExpiresAt,
  );
}

function sanitizePayment(payment = null) {
  if (!payment || typeof payment !== "object") return payment || null;

  return {
    ...payment,
    clientSecret: "",
    failureCode:
      payment.failureCode || payment.failureMessage ? "PAYMENT_FAILED" : "",
    failureMessage:
      payment.failureCode || payment.failureMessage
        ? "Payment could not be completed."
        : "",
  };
}

function sanitizeOrder(order = null) {
  if (!order || typeof order !== "object") return order || null;

  return {
    ...order,
    payment: sanitizePayment(order.payment),
  };
}

function getCurrentPayload(payload = {}) {
  const order = sanitizeOrder(payload?.order || null);
  const payment = sanitizePayment(payload?.payment || order?.payment || null);

  const approvalUrl = safeText(
    payload?.checkoutUrl ||
      payload?.approvalUrl ||
      payload?.approveUrl ||
      payload?.payerActionUrl ||
      payment?.checkoutUrl ||
      payment?.approvalUrl ||
      payment?.approveUrl ||
      payment?.payerActionUrl,
  );

  return {
    order,
    orderNumber: safeText(
      payload?.orderNumber || order?.orderNumber || payment?.orderNumber,
    ),
    payment,
    clientSecret: "",
    customerCheckout: getCustomerCheckout(payload),
    paymentWindowExpiresAt: getPaymentWindowExpiresAt(payload),
    checkoutUrl: approvalUrl,
    approvalUrl,
    approveUrl: safeText(
      payload?.approveUrl || payload?.approvalUrl || approvalUrl,
    ),
    payerActionUrl: safeText(payload?.payerActionUrl || approvalUrl),
    nextAction: payload?.nextAction || payment?.nextAction || null,
  };
}

export function paymentReducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.PAYMENT_CONFIG_REQUEST:
      return {
        ...state,
        config: requestBranch(state.config),
      };

    case actionTypes.PAYMENT_CONFIG_SUCCESS:
      return {
        ...state,
        config: successBranch(state.config, action.payload, {
          loaded: true,
          enabledProviders: Array.isArray(action.payload?.enabledProviders)
            ? action.payload.enabledProviders
            : [],
          stripeEnabled: Boolean(action.payload?.providers?.stripe?.enabled),
          stripePublicKey: getStripePublicKey(action.payload),
          stripePublishableKey: getStripePublicKey(action.payload),
          paypalEnabled: Boolean(action.payload?.providers?.paypal?.enabled),
          paypalClientId: getPayPalClientId(action.payload),
          sumupEnabled: getSumUpEnabled(action.payload),
          providers: action.payload?.providers || {},
        }),
      };

    case actionTypes.PAYMENT_CONFIG_FAIL:
      return {
        ...state,
        config: failBranch(state.config, action.payload),
      };

    case actionTypes.PAYMENT_CONFIG_RESET:
      return {
        ...state,
        config: initialState.config,
      };

    case actionTypes.PAYMENT_ORDER_GET_REQUEST:
      return {
        ...state,
        getOrderPayment: requestBranch(state.getOrderPayment),
      };

    case actionTypes.PAYMENT_ORDER_GET_SUCCESS: {
      const current = getCurrentPayload(action.payload);

      return {
        ...state,
        getOrderPayment: successBranch(state.getOrderPayment, action.payload, {
          ...current,
        }),
        current,
      };
    }

    case actionTypes.PAYMENT_ORDER_GET_FAIL:
      return {
        ...state,
        getOrderPayment: failBranch(state.getOrderPayment, action.payload),
      };

    case actionTypes.PAYMENT_ORDER_GET_RESET:
      return {
        ...state,
        getOrderPayment: initialState.getOrderPayment,
      };

    case actionTypes.PAYMENT_ORDER_CREATE_REQUEST:
      return {
        ...state,
        orderPayment: requestBranch(state.orderPayment),
      };

    case actionTypes.PAYMENT_ORDER_CREATE_SUCCESS: {
      const current = getCurrentPayload(action.payload);

      return {
        ...state,
        orderPayment: successBranch(state.orderPayment, action.payload, {
          ...current,
          reused: Boolean(action.payload?.reused),
          ignored: Boolean(action.payload?.ignored),
          idempotencyKey: safeText(action.payload?.idempotencyKey),
        }),
        current,
      };
    }

    case actionTypes.PAYMENT_ORDER_CREATE_FAIL:
      return {
        ...state,
        orderPayment: failBranch(state.orderPayment, action.payload),
      };

    case actionTypes.PAYMENT_ORDER_CREATE_RESET:
      return {
        ...state,
        orderPayment: initialState.orderPayment,
      };

    case actionTypes.PAYMENT_ORDER_CANCEL_REQUEST:
      return {
        ...state,
        cancelPending: requestBranch(state.cancelPending),
      };

    case actionTypes.PAYMENT_ORDER_CANCEL_SUCCESS: {
      const current = getCurrentPayload(action.payload);

      return {
        ...state,
        cancelPending: successBranch(state.cancelPending, action.payload, {
          order: current.order,
          orderNumber: current.orderNumber,
          payment: current.payment,
          customerCheckout: current.customerCheckout,
          paymentWindowExpiresAt: current.paymentWindowExpiresAt,
          paymentAction: action.payload?.paymentAction || null,
          idempotencyKey: safeText(action.payload?.idempotencyKey),
        }),
        current,
      };
    }

    case actionTypes.PAYMENT_ORDER_CANCEL_FAIL:
      return {
        ...state,
        cancelPending: failBranch(state.cancelPending, action.payload),
      };

    case actionTypes.PAYMENT_ORDER_CANCEL_RESET:
      return {
        ...state,
        cancelPending: initialState.cancelPending,
      };

    case actionTypes.PAYMENT_ORDER_CONFIRM_REQUEST:
      return {
        ...state,
        confirmPayment: requestBranch(state.confirmPayment),
      };

    case actionTypes.PAYMENT_ORDER_CONFIRM_SUCCESS: {
      const current = getCurrentPayload(action.payload);

      return {
        ...state,
        confirmPayment: successBranch(state.confirmPayment, action.payload, {
          ...current,
          idempotencyKey: safeText(action.payload?.idempotencyKey),
        }),
        current,
      };
    }

    case actionTypes.PAYMENT_ORDER_CONFIRM_FAIL:
      return {
        ...state,
        confirmPayment: failBranch(state.confirmPayment, action.payload),
      };

    case actionTypes.PAYMENT_ORDER_CONFIRM_RESET:
      return {
        ...state,
        confirmPayment: initialState.confirmPayment,
      };

    case actionTypes.PAYMENT_SET_CURRENT:
      return {
        ...state,
        current: {
          ...state.current,
          ...getCurrentPayload(action.payload || {}),
        },
      };

    case actionTypes.PAYMENT_CLEAR_CURRENT:
      return {
        ...state,
        current: initialState.current,
      };

    case actionTypes.PAYMENT_RESET_ERROR:
      return {
        ...state,
        config: { ...state.config, error: null, errorCode: "", status: 0 },
        getOrderPayment: {
          ...state.getOrderPayment,
          error: null,
          errorCode: "",
          status: 0,
        },
        orderPayment: {
          ...state.orderPayment,
          error: null,
          errorCode: "",
          status: 0,
        },
        cancelPending: {
          ...state.cancelPending,
          error: null,
          errorCode: "",
          status: 0,
        },
        confirmPayment: {
          ...state.confirmPayment,
          error: null,
          errorCode: "",
          status: 0,
        },
      };

    case actionTypes.PAYMENT_RESET_META:
      return {
        ...state,
        config: {
          ...state.config,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        getOrderPayment: {
          ...state.getOrderPayment,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        orderPayment: {
          ...state.orderPayment,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        cancelPending: {
          ...state.cancelPending,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
        confirmPayment: {
          ...state.confirmPayment,
          success: false,
          message: "",
          requestId: "",
          retryAfterSeconds: 0,
        },
      };

    case actionTypes.PAYMENT_RESET_STATE:
      return createInitialState();

    default:
      return state;
  }
}

export default paymentReducer;
